// src/admin/pages/reservation/ReservationTable.tsx
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
} from '@mui/icons-material';
import { Reservation } from '../../../enums/reservation';

interface ReservationTableProps {
  reservations: Reservation[];
  onDelete: (reservation: Reservation) => void;
  onApprove: (reservation: Reservation) => void;
  onReject: (reservation: Reservation) => void;
  deleteLoading: string;
  isDark?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

// A reservation can be approved/rejected once, while it's still pending a
// decision — once it's moved past that (further into the sales pipeline, or
// already decided/cancelled) the quick actions no longer apply.
const PENDING_DECISION_STATUSES = new Set(['New Inquiry', 'Contacted']);

// ── Theme-aware chip style helper — mirrors BranchTable's getSoftChipSx ──────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-label': { color: p.text },
  };
};

const STATUS_CHIP_COLOR: Record<string, string> = {
  'New Inquiry': 'info',
  Contacted: 'default',
  Approved: 'success',
  Rejected: 'error',
  'Test Drive Scheduled': 'warning',
  'Reserved (Deposit Paid)': 'warning',
  Sold: 'success',
  Cancelled: 'error',
  Lost: 'error',
};

const ReservationTable: React.FC<ReservationTableProps> = ({
  reservations,
  onDelete,
  onApprove,
  onReject,
  deleteLoading,
  isDark = false,
  canEdit = false,
  canDelete = false,
}) => {
  const formatDate = (d: any) => {
    if (!d) return '-';
    const date = d instanceof Date ? d : d?.toDate?.() || new Date(d);
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        height: '100%',
        '& .MuiTable-root': { minWidth: 900 },
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Customer</TableCell>
            <TableCell>Car</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Deposit</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Received</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {reservations.map((r) => (
            <TableRow
              key={r.id}
              hover
              sx={{
                '&:hover': { backgroundColor: 'action.hover' },
                '&:last-child td, &:last-child th': { borderBottom: 0 },
              }}
            >
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                    {r.customerName?.[0]?.toUpperCase() || '?'}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {r.customerName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.customerPhone}
                    </Typography>
                  </Box>
                </Stack>
              </TableCell>

              <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                  {r.carName || (r.modelName ? `${r.brandName ? r.brandName + ' ' : ''}${r.modelName} (any unit)` : '—')}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {r.inquiryType}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2">
                  {r.depositAmount ? r.depositAmount.toLocaleString() : '—'}
                </Typography>
              </TableCell>

              <TableCell>
                <Chip
                  label={r.status}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx(STATUS_CHIP_COLOR[r.status] || 'default', isDark)}
                />
              </TableCell>

              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(r.createdAt)}
                </Typography>
              </TableCell>

              <TableCell align="right">
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                  {canEdit && PENDING_DECISION_STATUSES.has(r.status) && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton onClick={() => onApprove(r)} size="small" color="success">
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton onClick={() => onReject(r)} size="small" color="error">
                          <RejectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {canDelete && (
                    <Tooltip title="Delete Reservation">
                      <IconButton
                        onClick={() => onDelete(r)}
                        disabled={deleteLoading === r.id}
                        size="small"
                        color="error"
                      >
                        {deleteLoading === r.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReservationTable;
